import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, ViewChild } from '@angular/core';
import { FullCalendarComponent, FullCalendarModule } from '@fullcalendar/angular';
import { CalendarOptions } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { MessageService } from 'primeng/api';
import { dateToIsoString } from '../../common/functions';
import { errorMsg } from '../../common/messages';

@Component({
  selector: 'app-calendar',
  standalone: true,
  imports: [FullCalendarModule, CommonModule],
  templateUrl: './calendar.component.html'
})
export class CalendarComponent implements OnInit, OnChanges {
  @ViewChild('calendarRef') calendarComponent!: FullCalendarComponent;
  @Input() minDate: Date = new Date();
  @Input() maxDate: Date = new Date();
  @Input() invalidDays: number[] = []; // 0 = Sunday, 6 = Saturday
  @Input() invalidDates: string[] = []; // 'YYYY-MM-DD' format
  @Input() selectedDates: string[] = []; // 'YYYY-MM-DD' format
  @Input() invalidRecurrenceDates: string[] = [];

  @Output() updateRecurrences = new EventEmitter<any>();

  calendarOptions: CalendarOptions = {
    plugins: [dayGridPlugin, interactionPlugin],
    initialView: 'dayGridMonth',
    headerToolbar: {
      left: 'prev,next today',
      center: 'title',
      right: ''
    },
    height: 'auto',
    selectable: true,
    selectAllow: (selectInfo) => this.isRangeSelectable(selectInfo),
    dateClick: (info) => this.handleDateClick(info),
    dayCellClassNames: (arg) => this.getDayCellClassNames(arg),
    events: []
  };

  constructor(private msgService: MessageService) {}

  ngOnInit() {}

  ngOnChanges(): void {
    this.updateEvents();
  }

  private updateEvents(): void {
    const green = getCssVar('--res-color-green');
    const red = getCssVar('--res-color-red');

    const selectedEvents = this.selectedDates?.map((dateStr) => ({
      start: dateStr,
      allDay: true,
      display: 'background',
      backgroundColor: green
    }));

    const invalidRecurrenceEvents = this.invalidRecurrenceDates?.map((dateStr) => ({
      start: dateStr,
      allDay: true,
      display: 'background',
      backgroundColor: red
    }));

    this.calendarOptions.events = [...selectedEvents, ...invalidRecurrenceEvents];
  }

  private isRangeSelectable(selectInfo: any): boolean {
    const date = new Date(selectInfo.start);
    while (date < selectInfo.end) {
      const dateStr = dateToIsoString(date);
      if (this.invalidDays.includes(date.getDay()) || this.invalidDates.includes(dateStr) || date < this.minDate || date > this.maxDate) {
        return false;
      }
      date.setDate(date.getDate() + 1);
    }
    return true;
  }

  private handleDateClick(info: any): void {
    const date: Date = info.date;
    const dateStr: string = dateToIsoString(date);

    if (this.invalidRecurrenceDates.includes(dateStr)) {
      this.invalidRecurrenceDates = this.invalidRecurrenceDates.filter((d) => d !== dateStr);
      this.updateEvents();
      this.updateRecurrences.emit({
        recurrenceDates: this.selectedDates,
        invalidRecurrenceDates: this.invalidRecurrenceDates
      });
      return;
    }

    if (date < this.minDate || date > this.maxDate || this.invalidDays.includes(date.getDay()) || this.invalidDates.includes(dateStr)) {
      errorMsg(this.msgService, 'This date cannot be selected');
      return;
    }

    if (this.selectedDates.includes(dateStr)) {
      this.selectedDates = this.selectedDates.filter((d) => d !== dateStr);
    } else {
      this.selectedDates = [...this.selectedDates, dateStr];
    }
    this.selectedDates = this.selectedDates.sort();

    this.updateRecurrences.emit({
      recurrenceDates: this.selectedDates,
      invalidRecurrenceDates: this.invalidRecurrenceDates
    });
    this.updateEvents();
  }

  private getDayCellClassNames(arg: any): string[] {
    const date: Date = arg.date;
    const dateStr: string = dateToIsoString(date);
    const classNames: string[] = [];

    if (this.invalidRecurrenceDates.includes(dateStr)) {
      classNames.push('fc-day-invalid-recurrence');
    }

    if (
      (this.invalidDays.includes(date.getDay()) || this.invalidDates.includes(dateStr) || date < this.minDate || date > this.maxDate) &&
      !this.invalidRecurrenceDates.includes(dateStr)
    ) {
      classNames.push('fc-day-disabled');
    }

    if (this.selectedDates.includes(dateStr)) {
      classNames.push('fc-day-selected');
    }

    return classNames;
  }
}

function getCssVar(varName: string): string {
  // trims whitespace which could be included in the value
  return getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
}
